<script>
  // @ts-check
  import DynamicHeading from "../atoms/DynamicHeading.svelte";
  import Grid from "../atoms/Grid.svelte";
  import GridContainer from "../atoms/GridContainer.svelte";
  import Image from "../atoms/Image.svelte";
  import { v4 as uuid } from "@lukeed/uuid";
  import { onMount, onDestroy } from "svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /**
   * Additional classes which apply to the container
   * @type { string }
   * */
  let className = "";
  export { className as class };

  /**
   * The title of the hero section
   * @type {string}
   */
  export let title;

  /**
   * The animated title of the hero section
   * @type {string}
   */
  export let animatedTitle = null;

  /**
   * The subtitle of the hero section
   * @type {string}
   */
  export let subtitle = null;

  /**
   * The featured image in the hero section
   * @type {import('../types/shared.js').Image}
   */
  export let featuredImage = null;

  let animation;
  let isAnimationReady = !animatedTitle;
  let splitTitle = title?.split(" ") || [];
  let animationClass = "ease-spring-easing transition-all duration-[520ms]";

  $: titleClass = isAnimationReady ? "[&>div>span]:translate-y-0" : "[&>div>span]:translate-y-full";
  $: titleSpanClass = isAnimationReady
    ? "grid-rows-[1fr] opacity-[1]"
    : "grid-rows-[0fr] opacity-[0]";
  $: containerAnimationClass = isAnimationReady ? "translate-y-[0]" : "translate-y-[10%]";
  $: imageContainerClass = isAnimationReady ? "translate-y-[0]" : "translate-y-[50%]";

  onMount(() => {
    if (animatedTitle) {
      animation = setTimeout(() => {
        isAnimationReady = true;
      }, 500);
    }
  });

  onDestroy(() => {
    clearInterval(animation);
  });
</script>

<GridContainer
  {id}
  containerElement="section"
  containerClass="mt-15 mb-15 md:mt-20 md:mb-20 lg:mb-25 xl:mt-25"
>
  <Grid gridClass="{containerAnimationClass} {animationClass} delay-[500ms] {className}">
    <DynamicHeading class="heading-xxl col-full-width uppercase" aria-label={title}>
      <div class="{titleClass} {animationClass} flex flex-wrap gap-x-2 overflow-hidden">
        {#each splitTitle as title (`${title}-${uuid()}`)}
          <div class=" overflow-hidden pr-2" aria-hidden={true}>
            <span class="{animationClass} block">
              {title}
            </span>
          </div>
        {/each}
      </div>
      {#if animatedTitle}
        <div class="{titleSpanClass} {animationClass} grid delay-[500ms]">
          <span class="overflow-hidden text-cobalt-blue">
            {animatedTitle}
          </span>
        </div>
      {/if}
    </DynamicHeading>
    {#if featuredImage}
      <div class="col-full-width overflow-hidden bg-white pt-7.5 lg:pt-12.5">
        <Image
          {...featuredImage}
          aspectRatioClasses="aspect-3/4 md:aspect-3/2"
          containerClass="{imageContainerClass} {animationClass}"
        />
        <!-- TODO: Add support for rich text / videos: https://github.com/ObamaFoundation/of-design-system/issues/89 -->
      </div>
    {/if}
    {#if subtitle}
      <p class="body-lg col-full-width pt-7.5 lg:pt-12.5">
        {subtitle}
      </p>
    {/if}
  </Grid>
</GridContainer>
